"use client";

import { useEffect, useState, type ReactNode } from "react";
import Link from "next/link";
import type { TrustSecurityData } from "@/types/analyze";
import { AHREFS_DOMAIN_RATING_ATTRIBUTION_URL } from "@/lib/domain-rating-constants";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { ExternalLink, Info, Lock } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ButtonCopyToLLM } from "@/components/ButtonCopyToLLM";

interface TrustSecurityOverviewProps {
  trustSecurity?: TrustSecurityData;
  defaultExpanded?: boolean;
  hasPaidAccess?: boolean;
  onRequireUpgrade?: () => void;
  showCopyButton?: boolean;
}

type TrustSecurityCheck = TrustSecurityData["checks"][number];
type LockedTrustSecurityCheck = Extract<TrustSecurityCheck, { locked: true }>;
type UnlockedTrustSecurityCheck = Exclude<
  TrustSecurityCheck,
  LockedTrustSecurityCheck
>;

const TRUST_CATEGORY_IDS = [
  "https",
  "hsts",
  "csp",
  "xFrameOptions",
  "xContentTypeOptions",
  "domainRating",
  "domainAge",
  "registrar",
  "country",
  "provider",
] as const;

type TrustCategoryId = (typeof TRUST_CATEGORY_IDS)[number];

const categories: ReadonlyArray<{
  title: string;
  order: TrustCategoryId[];
}> = [
  {
    title: "Security Headers",
    order: ["https", "hsts", "csp", "xFrameOptions", "xContentTypeOptions"],
  },
  {
    title: "Domain Trust",
    order: ["domainRating", "domainAge", "registrar", "country"],
  },
  {
    title: "Infrastructure",
    order: ["provider"],
  },
];

const checkDescriptions: Record<string, string> = {
  https:
    "Encrypted connection between browser and server. Essential for data protection and SEO.",
  hsts: "Forces browsers to use only HTTPS connections. Prevents downgrade attacks.",
  csp: "Prevents Cross-Site Scripting (XSS) attacks by controlling resource loading.",
  xFrameOptions:
    "Prevents clickjacking attacks by controlling if your site can be embedded in frames.",
  xContentTypeOptions:
    "Prevents MIME-type sniffing attacks by forcing browsers to respect declared content types.",
  domainRating:
    "Ahrefs Domain Rating based on the strength of the target's backlink profile.",
  domainAge:
    "How long the domain has been registered. Older domains are generally more trusted.",
  registrar: "The company that registered the domain.",
  country: "The country where the domain is registered.",
  provider: "The hosting or CDN provider for this website.",
};

const TRUST_SECURITY_GOAL =
  "Update the codebase so Trust & Security signals (headers, domain trust, infrastructure) meet full-score requirements.";

const TRUST_SECURITY_INSTRUCTIONS = [
  "Work only on the Trust & Security issues listed below.",
  "If an issue refers to an external metric (e.g., Domain Rating, Domain Age), do NOT modify code—log it under `unresolved` with a short explanation and recommended remediation steps.",
  "For code-controlled issues (headers, provider metadata, middleware, etc.), locate the relevant files and apply the fix directly with production-ready values.",
  "Do not add or change security controls that are not explicitly mentioned in the issues list.",
  "After editing, print the affected file paths plus a concise summary or diff of each change.",
  "If any issue still cannot be fully addressed, add it to `unresolved` describing the blocker and next steps.",
] as const;

const isTrustCategoryId = (id: string): id is TrustCategoryId =>
  (TRUST_CATEGORY_IDS as readonly string[]).includes(id);

const getCategoryForCheck = (checkId: string): string => {
  if (!isTrustCategoryId(checkId)) return "General";
  const category = categories.find((entry) => entry.order.includes(checkId));
  return category?.title ?? "General";
};

const getCheckStatus = (
  achieved: number,
  max: number
): "passed" | "warning" | "failed" => {
  if (max <= 0) return "passed";
  const ratio = achieved / max;
  if (ratio >= 0.8) return "passed";
  if (ratio >= 0.6) return "warning";
  return "failed";
};

function buildTrustSecurityCopyPayload(
  trustSecurity?: TrustSecurityData
): string {
  const checks = trustSecurity?.checks ?? [];
  const score = trustSecurity?.score ?? 0;

  const payload = {
    instructions: Array.from(TRUST_SECURITY_INSTRUCTIONS),
    section: "Trust & Security",
    score,
    goal: TRUST_SECURITY_GOAL,
    issues: [] as Array<{
      id: string;
      title: string;
      status: "failed" | "warning";
      observed: string;
      description: string;
      category: string;
      remediation?: string;
    }>,
    unresolved: [] as string[],
    notes: [] as string[],
  };

  if (!checks.length) {
    payload.notes.push(
      "Trust & Security data was not captured for this crawl."
    );
    return JSON.stringify(payload, null, 2);
  }

  const unlockedChecks = checks.filter(
    (check): check is UnlockedTrustSecurityCheck => !isLockedCheck(check)
  );

  if (!unlockedChecks.length) {
    payload.notes.push("Trust & Security checks are locked for this plan.");
    return JSON.stringify(payload, null, 2);
  }

  const needsWork = unlockedChecks.filter((check) => {
    const { normalized } = check;
    return normalized.max > 0 && normalized.achieved < normalized.max;
  });

  if (!needsWork.length) {
    payload.notes.push("All Trust & Security checks are currently passing.");
    return JSON.stringify(payload, null, 2);
  }

  needsWork.forEach((check) => {
    const status = getCheckStatus(
      check.normalized.achieved,
      check.normalized.max
    );
    if (status === "passed") return;

    const observation =
      typeof check.value === "string" && check.value.trim().length > 0
        ? check.value.trim()
        : check.note?.trim() ?? "Missing configuration.";

    payload.issues.push({
      id: check.id,
      title: check.title,
      status,
      observed: observation,
      description:
        checkDescriptions[check.id] ?? "Provide the required security detail.",
      category: getCategoryForCheck(check.id),
      remediation:
        check.id === "domainRating"
          ? "External metric. Improve through backlink acquisition and authority building; document under unresolved."
          : check.id === "domainAge"
          ? "External metric. Requires time and registrar history; document under unresolved."
          : undefined,
    });
  });

  if (payload.issues.length === 0) {
    payload.notes.push(
      "No Trust & Security issues were identified in the unlocked checks."
    );
  }

  if (!payload.notes.length) {
    delete (payload as { notes?: string[] }).notes;
  }
  if (!payload.unresolved.length) {
    delete (payload as { unresolved?: string[] }).unresolved;
  }

  return JSON.stringify(payload, null, 2);
}

export function TrustSecurityOverview({
  trustSecurity,
  defaultExpanded = true,
  hasPaidAccess = true,
  onRequireUpgrade,
  showCopyButton = false,
}: TrustSecurityOverviewProps) {
  const overallScore = trustSecurity?.score ?? 0;
  const trustSecurityChecks = trustSecurity?.checks ?? [];

  const unlockedChecks = trustSecurityChecks.filter(
    (check): check is UnlockedTrustSecurityCheck => !isLockedCheck(check)
  );

  const passedItemsCount = unlockedChecks.filter((check) => {
    if (check.normalized.max <= 0) return false;
    const successRatio = check.normalized.achieved / check.normalized.max;
    return successRatio >= 0.8;
  }).length;

  const warningItemsCount = unlockedChecks.filter((check) => {
    if (check.normalized.max <= 0) return false;
    const successRatio = check.normalized.achieved / check.normalized.max;
    return successRatio >= 0.6 && successRatio < 0.8;
  }).length;

  const failedItemsCount = unlockedChecks.filter((check) => {
    if (check.normalized.max <= 0) return false;
    const successRatio = check.normalized.achieved / check.normalized.max;
    return successRatio < 0.6;
  }).length;

  const getScoreColor = (score: number) => {
    if (score >= 80) return "var(--status-green)";
    if (score >= 60) return "var(--status-orange)";
    return "var(--status-red)";
  };

  return (
    <CollapsibleSection
      title="Trust & Security"
      subtitle={overallScore.toString()}
      subtitleColor={getScoreColor(overallScore)}
      rightContent={
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-status-green-bg text-status-green-foreground px-2 py-0.5 text-[11px] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-status-green" />
            {passedItemsCount}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-status-orange-bg text-status-orange-foreground px-2 py-0.5 text-[11px] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-status-orange" />
            {warningItemsCount}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-status-red-bg text-status-red-foreground px-2 py-0.5 text-[11px] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-status-red" />
            {failedItemsCount}
          </span>
        </div>
      }
      actionContent={
        showCopyButton ? (
          <ButtonCopyToLLM
            getCopyText={() => buildTrustSecurityCopyPayload(trustSecurity)}
            onBeforeCopy={() => {
              if (hasPaidAccess) return true;
              onRequireUpgrade?.();
              return false;
            }}
          />
        ) : undefined
      }
      defaultExpanded={defaultExpanded}
    >
      <div className="px-3 pb-4 space-y-6">
        {categories.map((category) => {
          const categoryChecks = category.order
            .map((id) => trustSecurityChecks.find((check) => check.id === id))
            .filter((check): check is TrustSecurityCheck => Boolean(check));

          if (categoryChecks.length === 0) {
            return null;
          }

          return (
            <div key={category.title}>
              <h3 className="text-base font-bold text-foreground mb-3">
                {category.title}
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {categoryChecks.map((check) =>
                  isLockedCheck(check)
                    ? renderLockedCard(check)
                    : renderTrustCheck(check)
                )}
              </div>
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}

function isLockedCheck(
  check: TrustSecurityCheck
): check is LockedTrustSecurityCheck {
  return (check as { locked?: boolean }).locked === true;
}

function renderLockedCard(check: LockedTrustSecurityCheck) {
  const description = checkDescriptions[check.id];

  return (
    <div
      key={check.id}
      className="flex items-start gap-3 p-3 rounded-lg border border-border/30 bg-card/20 h-full"
    >
      <span className="mt-1 inline-block w-2 h-2 rounded-full flex-none bg-muted-foreground/40" />
      <div className="min-w-0 flex-1 text-left flex flex-col h-full">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-foreground">
              {check.title}
            </div>
            {description && (
              <Popover>
                <PopoverTrigger asChild>
                  <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer" />
                </PopoverTrigger>
                <PopoverContent className="w-80 max-w-[90vw]">
                  <div className="space-y-2 text-left">
                    <p className="text-sm">{description}</p>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
            <Lock className="h-3 w-3" />
            Locked
          </span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Upgrade to unlock full details for this check.
        </p>
        {check.id === "domainRating" && <DomainRatingAttribution />}
      </div>
    </div>
  );
}

function renderTrustCheck(check: UnlockedTrustSecurityCheck) {
  const statusColor = getStatusColorFromNormalized(
    check.normalized.achieved,
    check.normalized.max
  );
  const description = checkDescriptions[check.id];
  const valueContent = getValueContent(check);

  return (
    <div
      key={check.id}
      className="flex items-start gap-3 p-3 rounded-lg border border-border/30 bg-card/20 h-full"
    >
      <span
        className={`mt-1 inline-block w-2 h-2 rounded-full flex-none ${statusColor}`}
      />
      <div className="min-w-0 flex-1 text-left flex flex-col h-full">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-foreground">
              {check.title}
            </div>
            {description && (
              <Popover>
                <PopoverTrigger asChild>
                  <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer" />
                </PopoverTrigger>
                <PopoverContent className="w-80 max-w-[90vw]">
                  <p className="text-sm">{description}</p>
                </PopoverContent>
              </Popover>
            )}
          </div>
          {check.normalized.max > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
              {check.normalized.achieved}/{check.normalized.max}
            </span>
          )}
        </div>
        {check.id === "domainRating" && (
          <div className="mb-2">
            <Link
              href="/guides/domain-rating"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card/40 px-2.5 py-1 text-xs font-medium text-foreground/80 transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
            >
              <span>How to improve DR</span>
              <span className="transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </Link>
          </div>
        )}
        {valueContent && (
          <div className="text-xs text-foreground/80 mb-3 break-words text-left leading-relaxed flex-1">
            {valueContent}
          </div>
        )}
        {check.id === "domainRating" && <DomainRatingAttribution />}
      </div>
    </div>
  );
}

function getValueContent(check: UnlockedTrustSecurityCheck): ReactNode {
  if (!check.value) {
    return null;
  }

  if (check.id === "domainRating") {
    const parsed = parseDomainRatingValue(check.value);
    const checkedAtValue = check.meta?.domainRatingUpdatedAt ?? parsed.updatedAt;

    if (parsed.rating !== null) {
      return (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-x-2 gap-y-1">
            <div className="text-xl font-semibold leading-none text-foreground tabular-nums">
              DR {formatDomainRatingDisplay(parsed.rating)}
            </div>
            <div className="pb-0.5 text-xs font-medium text-muted-foreground">
              /100
            </div>
          </div>
          <DomainRatingCheckedAt value={checkedAtValue} />
          <DomainRatingIndicator domainRating={parsed.rating} />
        </div>
      );
    }
  }

  return check.value;
}

function extractDomainRating(value: string): number | null {
  const match = value.match(/DR\s+(\d+(?:\.\d+)?)/i) ?? value.match(/\d+/);
  if (!match) return null;
  const parsed = Number(match[1] ?? match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseDomainRatingValue(value: string): {
  rating: number | null;
  updatedAt: string | null;
} {
  const updatedMatch = value.match(/(?:Updated|Last sync:)\s+(.+)$/i);

  return {
    rating: extractDomainRating(value),
    updatedAt: updatedMatch?.[1]?.trim() || null,
  };
}

function formatDomainRatingDisplay(value: number): string {
  return Number.isInteger(value) ? value.toString() : value.toFixed(1);
}

type FormattedDomainRatingTimestamp = {
  label: string;
  title: string;
  iso: string;
};

function DomainRatingCheckedAt({
  value,
}: {
  value: string | null | undefined;
}) {
  const [checkedAt, setCheckedAt] =
    useState<FormattedDomainRatingTimestamp | null>(null);

  useEffect(() => {
    setCheckedAt(formatDomainRatingTimestamp(value));
  }, [value]);

  if (!checkedAt) return null;

  return (
    <div className="text-xs leading-none text-muted-foreground">
      Checked{" "}
      <time dateTime={checkedAt.iso} title={checkedAt.title}>
        {checkedAt.label}
      </time>
    </div>
  );
}

function formatDomainRatingTimestamp(
  value: string | null | undefined
): FormattedDomainRatingTimestamp | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
  const title = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);

  if (isSameCalendarDate(date, now)) {
    return { label: `today at ${time}`, title, iso: date.toISOString() };
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDate(date, yesterday)) {
    return { label: `yesterday at ${time}`, title, iso: date.toISOString() };
  }

  return {
    label: new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date),
    title,
    iso: date.toISOString(),
  };
}

function isSameCalendarDate(first: Date, second: Date): boolean {
  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
}

function DomainRatingAttribution() {
  return (
    <Link
      href={AHREFS_DOMAIN_RATING_ATTRIBUTION_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-2 inline-flex w-fit items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <span>Domain Rating by Ahrefs</span>
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </Link>
  );
}

function getStatusColorFromNormalized(achieved: number, max: number): string {
  if (max <= 0) return "bg-muted-foreground/40";
  const successRatio = achieved / max;
  if (successRatio >= 0.8) return "bg-status-green";
  if (successRatio >= 0.6) return "bg-status-orange";
  return "bg-status-red";
}

function DomainRatingIndicator({ domainRating }: { domainRating: number }) {
  const maxRating = 100;
  const clampedValue = Math.max(0, Math.min(maxRating, domainRating));
  const positionPercentage = `${(clampedValue / maxRating) * 100}%`;

  const t1 = 15;
  const t2 = 25;
  const t1Percentage = (t1 / maxRating) * 100;
  const t2Percentage = (t2 / maxRating) * 100;
  const gapPixels = 6;

  const ratingZone =
    clampedValue >= t2 ? "emerald" : clampedValue >= t1 ? "amber" : "red";

  return (
    <div className="mt-4 relative w-full">
      <div className="relative h-2 w-full">
        <span
          className="absolute left-0 top-0 h-2 rounded-l-full rounded-r-full bg-status-red/90"
          style={{ width: `calc(${t1Percentage}% - ${gapPixels / 2}px)` }}
        />
        <span
          className="absolute top-0 h-2 bg-background rounded-full"
          style={{
            left: `calc(${t1Percentage}% - ${gapPixels / 2}px)`,
            width: `${gapPixels}px`,
          }}
        />
        <span
          className="absolute top-0 h-2 bg-status-orange/90 rounded-l-full rounded-r-full"
          style={{
            left: `calc(${t1Percentage}% + ${gapPixels / 2}px)`,
            width: `calc(${t2Percentage - t1Percentage}% - ${gapPixels}px)`,
          }}
        />
        <span
          className="absolute top-0 h-2 bg-background rounded-full"
          style={{
            left: `calc(${t2Percentage}% - ${gapPixels / 2}px)`,
            width: `${gapPixels}px`,
          }}
        />
        <span
          className="absolute top-0 h-2 rounded-l-full rounded-r-full bg-status-green/90"
          style={{
            left: `calc(${t2Percentage}% + ${gapPixels / 2}px)`,
            width: `calc(${100 - t2Percentage}% - ${gapPixels / 2}px)`,
          }}
        />

        <span
          className="pointer-events-none absolute -top-4 -translate-x-1/2 text-[10px] text-muted-foreground"
          style={{ left: `calc(${t1Percentage}%)` }}
        >
          15
        </span>
        <span
          className="pointer-events-none absolute -top-4 -translate-x-1/2 text-[10px] text-muted-foreground"
          style={{ left: `calc(${t2Percentage}%)` }}
        >
          25
        </span>

        <span
          className={`absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 -translate-x-1/2 rounded-full border bg-background shadow-sm ${
            ratingZone === "emerald"
              ? "border-emerald-500"
              : ratingZone === "amber"
              ? "border-amber-500"
              : "border-red-500"
          }`}
          style={{ left: positionPercentage }}
        />
      </div>
    </div>
  );
}
